import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center transition-colors disabled:pointer-events-none disabled:opacity-50 font-medium",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 border-primary shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
        outline:
          "bg-background text-foreground border border-input bg-white hover:bg-accent hover:text-accent-foreground shadow-sm",
        ghost: "hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "py-[0.5rem] px-[0.875rem] rounded text-sm",
        lg: "py-[0.625rem] px-[1rem] rounded text-sm",
        sm: "py-[0.5rem] px-[0.75rem] rounded text-xs",
        xs: "py-[0.25rem] px-[0.75rem] rounded-lg text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon?: React.ReactNode;
  loading?: boolean;
  asChild?: boolean;
  testId?: string;
  label?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      children,
      icon,
      loading = false,
      asChild = false,
      testId,
      label,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || props.disabled}
        data-testid={testId}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                data-testid="loader"
              />
            ) : (
              icon && <span className="mr-2">{icon}</span>
            )}
            {children || label}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
